import { buildApiEndpoint } from "../hooks/useAppBootstrapFlow";
import { LocalizedMessage } from "../i18n/LocaleContext";

/**
 * Cliente das rotas /api/bearer/tenantAdmin — a administração dos usuários do próprio tenant,
 * disponível só para quem tem `permissions.tenantAdmin`.
 *
 * Nenhuma função daqui envia `tenantId` ou `userId`: o backend os toma do token SSO e ignora o
 * que vier no corpo. Por isso a tela não tem (nem precisa de) a noção de "qual cliente" — é
 * sempre o do usuário autenticado.
 */

/** O envelope de autenticação do app: token SSO no Authorization e uma repetição no 401. */
export type AuthorizedFetch = (url: string, init: RequestInit, body?: string) => Promise<Response>;

export type TenantAdminSummary = {
  clientName: string;
  license: number;
  licensedCount: number;
};

export type TenantAdminUser = {
  id: string;
  tenantUserData: { id: string; displayName: string; mail: string };
  extension: string;
  licenced: boolean;
  permissions: { recordAccess: boolean; pabxContactsAccess: boolean; tenantAdmin: boolean };
};

/** Usuário do diretório do tenant (Graph), para o diálogo de cadastro. */
export type TenantDirectoryUser = {
  id: string;
  displayName?: string;
  mail?: string;
};

export type TenantAdminPatch = {
  extension?: string;
  licenced?: boolean;
  recordAccess?: boolean;
  pabxContactsAccess?: boolean;
  tenantAdmin?: boolean;
};

/**
 * Falha numa requisição de administração, já traduzida para uma mensagem da interface.
 *
 * O corpo do backend não é exibido: as mensagens dele são em português fixo e a tela é
 * localizada em oito idiomas. O `reason` é o que distingue os três conflitos que compartilham o
 * status 409 — ramal em uso, licenças esgotadas e alteração sobre si mesmo.
 */
export class TenantAdminError extends Error {
  readonly status: number;
  readonly message$: LocalizedMessage;

  constructor(status: number, message$: LocalizedMessage) {
    super(`tenantAdmin request failed with status ${status}`);
    this.name = "TenantAdminError";
    this.status = status;
    this.message$ = message$;
  }
}

type ConflictReason = "extension" | "licence" | "self";

const CONFLICT_MESSAGES: Record<ConflictReason, LocalizedMessage["key"]> = {
  extension: "tenantAdmin.error.extensionConflict",
  licence: "tenantAdmin.error.licenseLimit",
  self: "tenantAdmin.error.selfChange",
};

async function toError(response: Response): Promise<TenantAdminError> {
  if (response.status === 403) {
    return new TenantAdminError(response.status, { key: "tenantAdmin.error.forbidden" });
  }
  if (response.status === 409) {
    const body = (await response.json().catch(() => ({}))) as { reason?: ConflictReason };
    const key = (body.reason && CONFLICT_MESSAGES[body.reason]) || "tenantAdmin.error.generic";
    return new TenantAdminError(response.status, { key });
  }
  return new TenantAdminError(response.status, {
    key: "tenantAdmin.error.requestFailed",
    params: { status: response.status },
  });
}

async function request<T>(
  authorizedFetch: AuthorizedFetch,
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await authorizedFetch(
    buildApiEndpoint(`/api/bearer/tenantAdmin${path}`),
    { method, signal },
    body === undefined ? undefined : JSON.stringify(body)
  );
  if (!response.ok) {
    throw await toError(response);
  }
  return (await response.json()) as T;
}

export function fetchTenantAdminSummary(authorizedFetch: AuthorizedFetch, signal?: AbortSignal) {
  return request<TenantAdminSummary>(authorizedFetch, "GET", "/summary", undefined, signal);
}

export function fetchTenantAdminUsers(authorizedFetch: AuthorizedFetch, signal?: AbortSignal) {
  return request<TenantAdminUser[]>(authorizedFetch, "GET", "/appUsers", undefined, signal);
}

export function patchTenantAdminUser(
  authorizedFetch: AuthorizedFetch,
  appUserId: string,
  changes: TenantAdminPatch
) {
  return request<TenantAdminUser>(authorizedFetch, "PATCH", `/appUsers/${encodeURIComponent(appUserId)}`, changes);
}

export function createTenantAdminUser(authorizedFetch: AuthorizedFetch, userId: string) {
  return request<TenantAdminUser>(authorizedFetch, "POST", "/appUsers", { userId });
}

export function deleteTenantAdminUser(authorizedFetch: AuthorizedFetch, appUserId: string) {
  return request<{ message: string }>(
    authorizedFetch,
    "DELETE",
    `/appUsers/${encodeURIComponent(appUserId)}`
  );
}

// Teto defensivo contra um gateway que devolvesse `@odata.nextLink` indefinidamente — o mesmo
// motivo do PAGE_SAFETY_CAP em pabxApi.ts e do MAX_TEAMS_CONTACTS em ContactSearch.
const DIRECTORY_PAGE_CAP = 50;

/**
 * Diretório do tenant, página a página, seguindo o `@odata.nextLink` relativo que o backend
 * devolve. A lista inteira é necessária: a busca do diálogo de cadastro é um filtro local.
 */
export async function fetchTenantDirectory(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<TenantDirectoryUser[]> {
  const users: TenantDirectoryUser[] = [];
  let path: string | null = "/tenantUsers/page?$top=100";

  for (let page = 0; page < DIRECTORY_PAGE_CAP && path; page += 1) {
    const body: { value?: TenantDirectoryUser[]; "@odata.nextLink"?: string } = await request(
      authorizedFetch,
      "GET",
      path,
      undefined,
      signal
    );
    users.push(...(body.value ?? []));
    // O nextLink já vem com o caminho completo da rota admin; `request` prefixa, então só a
    // parte depois de /tenantAdmin interessa.
    const nextLink = body["@odata.nextLink"];
    path = nextLink ? nextLink.replace(/^.*\/tenantAdmin/, "") : null;
  }

  return users;
}
